import type { AreaGuide } from "./types"

export const BRIGHTON_AREA_GUIDES: AreaGuide[] = [
  {
    slug: "brighton",
    name: "Brighton",
    location: "brighton",
    tagline: "Seaside city with endless personality",
    description:
      "Brighton city centre is a vibrant mix of Regency architecture, independent shops, cultural venues, and a famous seafront. The Lanes and North Laine offer boutique shopping, while the seafront stretches from the Marina to Hove Lawns.\n\nProperty ranges from Regency townhouses and period conversions to modern apartments. The city centre suits professionals and couples who want walkable access to restaurants, nightlife, and the beach. Rental demand is consistently strong.",
    avgPrice: "£375,000",
    priceRange: "£200,000 - £1,500,000",
    propertyTypes: ["Regency townhouses", "Period conversions", "Modern apartments", "Seafront flats"],
    transport: {
      nearestStation: "Brighton (direct to London Victoria, 55 mins)",
      commuteTime: "55 mins to London Victoria",
      busRoutes: "Extensive local network across the city",
      parking: "Limited and expensive; permit zones throughout",
    },
    lifestyle: {
      restaurants: "64 Degrees, The Salt Room, Terre a Terre",
      schools: "Brighton College, BHASVIC, Varndean",
      greenSpaces: "Brighton seafront, The Level, Preston Park",
      shopping: "The Lanes, North Laine, Churchill Square",
    },
    highlights: [
      "Iconic seafront and pier",
      "The Lanes and North Laine shopping",
      "Direct train to London in under an hour",
      "Thriving food and drink scene",
      "Strong cultural and arts community",
    ],
    bestFor: ["Professionals", "Couples", "Investors"],
    imageUrl: "/images/areas/brighton/brighton-800w.webp",
  },
  {
    slug: "hove",
    name: "Hove",
    location: "brighton",
    tagline: "Brighton's quieter, more refined neighbour",
    description:
      "Hove offers a calmer alternative to Brighton with wide, tree-lined avenues, Regency and Victorian architecture, and a long seafront promenade. Church Road and George Street form the commercial heart, with independent shops, cafes, and restaurants.\n\nThe area attracts families and professionals who want seaside living with more space and a slightly more relaxed pace. Property includes grand period houses, mansion flats, and modern seafront developments.",
    avgPrice: "£450,000",
    priceRange: "£225,000 - £2,000,000",
    propertyTypes: ["Regency houses", "Victorian villas", "Mansion flats", "Seafront apartments"],
    transport: {
      nearestStation: "Hove (direct to London, 60 mins)",
      commuteTime: "60 mins to London Victoria",
      busRoutes: "1, 2, 5, 49 along the seafront and to Brighton",
      parking: "Residents' permit zones; slightly easier than Brighton",
    },
    lifestyle: {
      restaurants: "The Ginger Pig, The Gingerman, Fourth and Church",
      schools: "Hove Park School, West Hove Primary, Brighton & Hove High",
      greenSpaces: "Hove Lawns, Hove Park, Western Esplanade",
      shopping: "Church Road and George Street independents",
    },
    highlights: [
      "Wide, tree-lined avenues and grand architecture",
      "Hove Lawns and seafront promenade",
      "Church Road independent shopping",
      "More space and quieter than central Brighton",
      "Strong family-oriented community",
    ],
    bestFor: ["Families", "Professionals", "Downsizers"],
    imageUrl: "/images/areas/brighton/hove-800w.webp",
  },
  {
    slug: "kemptown",
    name: "Kemptown",
    location: "brighton",
    tagline: "Eclectic, characterful, and full of life",
    description:
      "Kemptown is one of Brighton's most distinctive neighbourhoods — colourful, inclusive, and full of independent character. St James's Street is the main artery, lined with vintage shops, cafes, pubs, and galleries. The area runs down to the Marina and seafront.\n\nProperty is predominantly Regency and Victorian, with a mix of terraced houses, flats, and period conversions. Kemptown suits buyers who want personality, community, and a slightly more affordable entry point to Brighton's seafront living.",
    avgPrice: "£325,000",
    priceRange: "£175,000 - £900,000",
    propertyTypes: ["Regency terraces", "Victorian houses", "Period conversions", "Sea-view flats"],
    transport: {
      nearestStation: "Brighton (15 mins walk)",
      commuteTime: "15 mins walk to Brighton station",
      busRoutes: "1, 1A, 7, 14, 27 along the seafront",
      parking: "Limited; permit zones in most streets",
    },
    lifestyle: {
      restaurants: "Bincho Yakitori, The Plotting Parlour, Busby & Wilds",
      schools: "St Mark's Primary, Queen's Park Primary",
      greenSpaces: "Queen's Park, Kemptown Enclosures, seafront",
      shopping: "St James's Street independents, Marina shops",
    },
    highlights: [
      "Vibrant, inclusive community atmosphere",
      "Brighton Marina and seafront access",
      "Strong independent food and drink scene",
      "More affordable than central Brighton",
      "Annual Kemptown Carnival and community events",
    ],
    bestFor: ["First-time buyers", "Young professionals", "Creative types"],
    imageUrl: "/images/areas/brighton/kemptown-800w.webp",
  },
  {
    slug: "preston-park",
    name: "Preston Park",
    location: "brighton",
    tagline: "Green space and Victorian grandeur",
    description:
      "Preston Park is centred around Brighton's largest urban park, with wide open green spaces, a velodrome, and a walled garden. The residential streets surrounding the park feature some of Brighton's finest Victorian and Edwardian houses.\n\nThe area has excellent transport links (Preston Park station has direct trains to London) and a growing cafe and restaurant scene along Preston Road. It's popular with families and commuters who want space and greenery without leaving the city.",
    avgPrice: "£425,000",
    priceRange: "£200,000 - £1,100,000",
    propertyTypes: ["Victorian terraces", "Edwardian semis", "Period conversions", "New builds"],
    transport: {
      nearestStation: "Preston Park (direct to London, 55 mins)",
      commuteTime: "55 mins to London Victoria",
      busRoutes: "5, 5A, 46, 49 to city centre",
      parking: "Mostly residents' permits; some unrestricted streets",
    },
    lifestyle: {
      restaurants: "Preston Park Tavern, The Park Crescent",
      schools: "Balfour Primary, Stanford Junior, Varndean",
      greenSpaces: "Preston Park (63 acres), Rockery, Walled Garden",
      shopping: "Preston Road local shops and cafes",
    },
    highlights: [
      "63-acre Preston Park with velodrome and walled garden",
      "Direct London trains from Preston Park station",
      "Impressive Victorian and Edwardian housing",
      "Growing local cafe and restaurant scene",
      "Annual Preston Park events and festivals",
    ],
    bestFor: ["Families", "Commuters", "Professionals"],
    imageUrl: "/images/areas/brighton/preston-park-800w.webp",
  },
  {
    slug: "rottingdean",
    name: "Rottingdean",
    location: "brighton",
    tagline: "Coastal village charm on Brighton's doorstep",
    description:
      "Rottingdean is a picturesque coastal village east of Brighton, retaining much of its historic character despite being absorbed into the city. The village green, pond, medieval church, and Tudor manor house create a distinctly rural feel, while the undercliff walk connects to Brighton Marina.\n\nProperty includes period cottages, flint-fronted houses, and modern family homes. Rottingdean suits buyers who want a quieter pace of life with easy access to Brighton by bus or the undercliff walk.",
    avgPrice: "£475,000",
    priceRange: "£225,000 - £1,500,000",
    propertyTypes: ["Period cottages", "Flint-fronted houses", "Detached family homes", "Bungalows"],
    transport: {
      nearestStation: "Brighton (bus, 20 mins)",
      commuteTime: "20 mins to Brighton by bus",
      busRoutes: "2, 22 to Brighton via coast road",
      parking: "Generally available; mix of on and off-street",
    },
    lifestyle: {
      restaurants: "The White Horse, The Plough, Rottingdean Club",
      schools: "Our Lady of Lourdes Primary, Rudyard Kipling Primary",
      greenSpaces: "Rottingdean village green, undercliff walk, Beacon Hill",
      shopping: "Village shops, galleries, and antique dealers",
    },
    highlights: [
      "Preserved village green and historic pond",
      "Undercliff walk to Brighton Marina",
      "Former home of Rudyard Kipling",
      "Independent galleries and antique shops",
      "Protected cliff-top views and beaches",
    ],
    bestFor: ["Families", "Retirees", "Downsizers"],
    imageUrl: "/images/areas/brighton/rottingdean-800w.webp",
  },
  {
    slug: "seven-dials",
    name: "Seven Dials",
    location: "brighton",
    tagline: "Central, walkable, and full of independents",
    description:
      "Seven Dials is a popular residential area just north of Brighton city centre, named after the seven-road junction at its heart. The area has a strong local identity with independent shops, cafes, and restaurants, and is within walking distance of both the station and the seafront.\n\nProperty is predominantly Victorian terraces and period conversions, offering good value compared to the seafront. The area attracts professionals, couples, and first-time buyers who want central living with neighbourhood character.",
    avgPrice: "£350,000",
    priceRange: "£180,000 - £800,000",
    propertyTypes: ["Victorian terraces", "Period conversions", "Maisonettes"],
    transport: {
      nearestStation: "Brighton (10 mins walk)",
      commuteTime: "10 mins walk to Brighton station",
      busRoutes: "Multiple routes through the junction",
      parking: "Permit zones; limited availability",
    },
    lifestyle: {
      restaurants: "The Chimney House, Smorls, Mange Tout",
      schools: "Middle Street Primary, St Bartholomew's Primary",
      greenSpaces: "Dyke Road Park, The Level (nearby)",
      shopping: "Seven Dials junction independents",
    },
    highlights: [
      "Central location within walking distance of everything",
      "Strong independent shopping and dining",
      "Victorian period character",
      "Good value compared to seafront areas",
      "Thriving local community",
    ],
    bestFor: ["First-time buyers", "Professionals", "Couples"],
    imageUrl: "/images/areas/brighton/seven-dials-800w.webp",
  },
]
